import { Colors, Spacing } from '@kvika/audur-theme'
import { asText } from '@prismicio/helpers'
import { useScroll } from 'motion/react'
import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { ProductSchema } from '@kvika/audur-api-types'

import { GridItem, MainGridContainer } from '../components/grid-container'
import BannerCarousel from '../container/banner-carousel/BannerCarousel'
import Calculator from '../container/calculator/Calculator'
import AudurWelcomeContent from '../container/content-blocks/AudurWelcomeContent'
import FAQTabUpdated from '../container/faq/FaqTabUpdated'
import Layout from '../container/layout/Layout'
import AudurPrismicClient from '../prismic/prismicClient'
import {
	AudurContentPage,
	AudurContentPageEditorialContentGroup,
	AudurFooter,
	AudurHeader,
	AudurMarketInterestRatesConnectionConnection,
	FaqCategoryConnectionConnection,
	Maybe,
} from '../prismic/prismicTypes'
import { valueOrDefault } from '../prismic/prismicUtils'
import { ANSWERS_CONTAINER_ID, prismicConstants, SITE_ALERT_REVALIDATE_SECONDS } from '../utils/Constants'
import { Route } from '../utils/navigation/Routes'
import { TranslatedStrings } from '../utils/translations/TranslatedStrings'
import { ColumnClass, Gutters, MetaTagInfo, PreviewData, SiteAlert } from '../utils/types/Types'
import {
	executeScrollToElement,
	getAudurApiClient,
	getAudurFooter,
	getAudurHeader,
	getAudurSiteAlert,
	getMetaTags,
} from '../utils/Utils'

type Props = {
	allAudurProducts: ProductSchema[]
	allAudurMarketInterestRates: Maybe<AudurMarketInterestRatesConnectionConnection>
	allFAQCategories: Maybe<FaqCategoryConnectionConnection>
	audurContentPage: Maybe<AudurContentPage>
	metaTags: MetaTagInfo
	footer: AudurFooter
	header: AudurHeader
	siteAlert: SiteAlert
}

const Home = ({
	allAudurProducts,
	allAudurMarketInterestRates,
	allFAQCategories,
	audurContentPage,
	metaTags,
	footer,
	header,
	siteAlert,
}: Props) => {
	const router = useRouter()
	const { home, faq } = TranslatedStrings.is
	const [isLottiePaused, setLottiePaused] = useState(false)

	const isMobile = useMediaQuery({ query: '(max-width: 599px)' })
	const isTablet = useMediaQuery({ query: '(max-width: 1024px)' })
	const navigateToFAQ = (tab: string) => {
		router
			.push({
				pathname: `${Route.Faq}/${tab}`,
			})
			.then(() => executeScrollToElement(ANSWERS_CONTAINER_ID))
	}
	const minLottieImageY = 0.28
	const { scrollYProgress } = useScroll()

	useMemo(() => {
		return scrollYProgress.on('change', (y) => {
			setLottiePaused(y > minLottieImageY)
		})
	}, [scrollYProgress])

	return (
		<Layout title={home.title} metaTags={metaTags} footer={footer} header={header} siteAlert={siteAlert}>
			<MainGridContainer
				backgroundColor={Colors.DarkGray}
				gutters={Gutters.LeftOnly}
				style={{ paddingBottom: 4, height: '620px' }}
			>
				<BannerCarousel
					groups={
						audurContentPage?.editorialContentGroup?.map((group) => {
							return group as AudurContentPageEditorialContentGroup
						}) ?? []
					}
					isLottiePaused={isLottiePaused}
					isMobile={isMobile}
					isTablet={isTablet}
					color={Colors}
					valueOrDefault={valueOrDefault}
				/>
			</MainGridContainer>
			<MainGridContainer backgroundColor={Colors.Silver} gutters={Gutters.Both} style={{ paddingBottom: 4 }}>
				<GridItem
					className={ColumnClass.All}
					style={{
						justifyContent: 'center',
						paddingTop: isMobile ? Spacing.Medium : Spacing.XLarge,
						paddingBottom: Spacing.XLarge,
					}}
				>
					{allAudurProducts && allAudurMarketInterestRates ? (
						<Calculator allAudurProducts={allAudurProducts} allAudurMarketInterestRates={allAudurMarketInterestRates} />
					) : null}
				</GridItem>
			</MainGridContainer>
			<MainGridContainer
				backgroundColor={Colors.DarkGray}
				gutters={Gutters.Both}
				style={{ marginTop: 120, marginBottom: 120 }}
			>
				<>
					{allFAQCategories?.edges?.map((maybeEdge, index) => {
						const categoryTitle = asText(maybeEdge?.node.title)
						const categoryImgSrc = maybeEdge?.node.image.url ?? ''
						if (categoryTitle) {
							return (
								<FAQTabUpdated
									key={index}
									tabTitle={maybeEdge?.node._meta.uid ?? ''}
									text={categoryTitle}
									imgSrc={categoryImgSrc}
									handleSetSelectedTab={() => navigateToFAQ(maybeEdge?.node._meta.uid ?? '')}
								/>
							)
						}
						return null
					})}
					<FAQTabUpdated
						tabTitle='hafa-samband'
						text={faq.contact}
						imgSrc='/svg/hafa-samband.svg'
						handleSetSelectedTab={() => router.push(Route.Contact)}
					/>
				</>
			</MainGridContainer>
			<AudurWelcomeContent />
		</Layout>
	)
}

export default Home

export const getStaticProps: GetStaticProps = async ({ previewData, locale }) => {
	const audurClient = new AudurPrismicClient(prismicConstants.apiToken)
	const uId = 'forsida'

	// No try/catch: a thrown error keeps ISR serving the last good page instead of caching a 404
	const allAudurMarketInterestRates = await audurClient.getMarketInterestRates({
		variables: { lang: prismicConstants.apiDefaultLocale },
	})

	const allFAQCategories = await audurClient.getAllFAQCategories({
		variables: {
			lang: prismicConstants.apiDefaultLocale,
		},
	})

	const audurContentPage = await audurClient.getAudurContentPageByUid({
		previewData: previewData as PreviewData,
		variables: { uId, lang: locale },
	})

	const [footer, header, siteAlert] = await Promise.all([getAudurFooter(), getAudurHeader(), getAudurSiteAlert()])

	const metaTags = getMetaTags({
		...audurContentPage,
		__typename: 'AudurContentPage',
	} as Maybe<AudurContentPage>)
	const apiClient = getAudurApiClient()

	return {
		props: {
			allAudurProducts: await apiClient.getProducts(),
			allAudurMarketInterestRates,
			allFAQCategories,
			audurContentPage,
			metaTags,
			footer,
			header,
			siteAlert,
		},
		revalidate: SITE_ALERT_REVALIDATE_SECONDS,
	}
}
